import { useState, useEffect } from 'react';
import { db } from '../../config/FirebaseConfig';
import { collection, addDoc, deleteDoc, doc, getDocs, updateDoc } from 'firebase/firestore';
import { FiPlus, FiTrash2, FiEdit2, FiX } from 'react-icons/fi';

const AdminEcosystem = () => {
  const [ecosystems, setEcosystems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    number: '',
    pillar: '',
    title: '',
    description: '',
    note: '',
    ctaLabel: '',
    ctaLink: '',
    isPrimary: false,
  });

  useEffect(() => {
    fetchEcosystems();
  }, []);

  const fetchEcosystems = async () => {
    try {
      setLoading(true);
      const ecosystemCollection = collection(db, 'ecosystem');
      const snapshot = await getDocs(ecosystemCollection);
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      // Ordenar por número
      setEcosystems(
        data.sort((a, b) => parseInt(a.number || 0) - parseInt(b.number || 0))
      );
    } catch (error) {
      console.error('Error al obtener empresas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !formData.number.trim() ||
      !formData.pillar.trim() ||
      !formData.title.trim() ||
      !formData.description.trim()
    ) {
      alert('Por favor completa los campos requeridos');
      return;
    }

    try {
      setLoading(true);
      if (editingId) {
        await updateDoc(doc(db, 'ecosystem', editingId), {
          ...formData,
          updatedAt: new Date(),
        });
        alert('Empresa actualizada correctamente');
      } else {
        await addDoc(collection(db, 'ecosystem'), {
          ...formData,
          createdAt: new Date(),
        });
        alert('Empresa creada correctamente');
      }
      setFormData({
        number: '',
        pillar: '',
        title: '',
        description: '',
        note: '',
        ctaLabel: '',
        ctaLink: '',
        isPrimary: false,
      });
      setEditingId(null);
      setShowForm(false);
      fetchEcosystems();
    } catch (error) {
      console.error('Error:', error);
      alert('Error al guardar la empresa');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta empresa?')) return;

    try {
      setLoading(true);
      await deleteDoc(doc(db, 'ecosystem', id));
      alert('Empresa eliminada correctamente');
      fetchEcosystems();
    } catch (error) {
      console.error('Error:', error);
      alert('Error al eliminar la empresa');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (ecosystem) => {
    setFormData({
      number: ecosystem.number,
      pillar: ecosystem.pillar,
      title: ecosystem.title,
      description: ecosystem.description,
      note: ecosystem.note || '',
      ctaLabel: ecosystem.cta?.label || '',
      ctaLink: ecosystem.cta?.href || '',
      isPrimary: ecosystem.primary || false,
    });
    setEditingId(ecosystem.id);
    setShowForm(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
            Gestión de Empresas/Pilares
          </h2>
          <p className="text-slate-600 dark:text-slate-400 text-sm mt-1">
            Administra los elementos del flujo de ecosistema
          </p>
        </div>
        <button
          onClick={() => {
            setShowForm(!showForm);
            if (showForm) setEditingId(null);
            setFormData({
              number: '',
              pillar: '',
              title: '',
              description: '',
              note: '',
              ctaLabel: '',
              ctaLink: '',
              isPrimary: false,
            });
          }}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg font-semibold transition-colors"
        >
          <FiPlus size={20} />
          Agregar Empresa
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              {editingId ? 'Editar Empresa' : 'Nueva Empresa'}
            </h3>
            <button
              onClick={() => {
                setShowForm(false);
                setEditingId(null);
              }}
              className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
            >
              <FiX size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                  Número (01, 02, 03...) *
                </label>
                <input
                  type="text"
                  value={formData.number}
                  onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                  placeholder="01"
                  maxLength="2"
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                  Pilar (ej: Pilar 1) *
                </label>
                <input
                  type="text"
                  value={formData.pillar}
                  onChange={(e) => setFormData({ ...formData, pillar: e.target.value })}
                  placeholder="Nombre del pilar"
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Título *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Nombre de la empresa o proyecto"
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Descripción *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe la empresa o proyecto"
                rows={4}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Nota (opcional)
              </label>
              <textarea
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                placeholder="Información adicional o quote"
                rows={3}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                  Texto del Botón CTA
                </label>
                <input
                  type="text"
                  value={formData.ctaLabel}
                  onChange={(e) => setFormData({ ...formData, ctaLabel: e.target.value })}
                  placeholder="ej: Explorar"
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                  Enlace del Botón CTA
                </label>
                <input
                  type="url"
                  value={formData.ctaLink}
                  onChange={(e) => setFormData({ ...formData, ctaLink: e.target.value })}
                  placeholder="https://ejemplo.com"
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isPrimary"
                checked={formData.isPrimary}
                onChange={(e) => setFormData({ ...formData, isPrimary: e.target.checked })}
                className="w-4 h-4 accent-indigo-500 cursor-pointer"
              />
              <label htmlFor="isPrimary" className="ml-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
                Marcar como principal (destacada)
              </label>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  setFormData({
                    number: '',
                    pillar: '',
                    title: '',
                    description: '',
                    note: '',
                    ctaLabel: '',
                    ctaLink: '',
                    isPrimary: false,
                  });
                }}
                className="px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
              >
                {loading ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Ecosystems List */}
      {loading && !showForm ? (
        <div className="text-center py-8 text-slate-600 dark:text-slate-400">
          Cargando empresas...
        </div>
      ) : ecosystems.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 dark:bg-slate-800 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700">
          <p className="text-slate-600 dark:text-slate-400 mb-4">
            No hay empresas/pilares configurados aún
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg font-semibold transition-colors"
          >
            <FiPlus size={20} />
            Agregar primera empresa
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {ecosystems.map((ecosystem) => (
            <div
              key={ecosystem.id}
              className={`
                rounded-xl p-6 border-2 hover:shadow-lg transition-all
                ${
                  ecosystem.primary
                    ? 'bg-indigo-50 dark:bg-indigo-950/30 border-indigo-300 dark:border-indigo-700'
                    : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                }
              `}
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-baseline gap-3">
                  <span className="text-4xl font-bold text-indigo-500">{ecosystem.number}</span>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">
                      {ecosystem.pillar}
                    </p>
                  </div>
                </div>
                {ecosystem.primary && (
                  <span className="px-2 py-1 bg-indigo-500 text-white text-xs font-bold rounded">
                    Principal
                  </span>
                )}
              </div>

              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                {ecosystem.title}
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                {ecosystem.description}
              </p>

              {ecosystem.note && (
                <p className="text-xs italic text-slate-500 dark:text-slate-400 mb-3 border-l-2 border-indigo-400 pl-2">
                  "{ecosystem.note}"
                </p>
              )}

              {ecosystem.ctaLabel && ecosystem.ctaLink && (
                <div className="mb-3 p-2 bg-slate-100 dark:bg-slate-700 rounded text-sm">
                  <span className="font-semibold text-slate-700 dark:text-slate-300">CTA:</span>{' '}
                  <a
                    href={ecosystem.ctaLink}
                    className="text-indigo-600 dark:text-indigo-400 hover:underline truncate"
                  >
                    {ecosystem.ctaLabel}
                  </a>
                </div>
              )}

              <div className="flex gap-2 pt-3 border-t border-slate-200 dark:border-slate-700">
                <button
                  onClick={() => handleEdit(ecosystem)}
                  className="flex-1 p-2 hover:bg-blue-100 dark:hover:bg-blue-950 text-blue-600 dark:text-blue-400 rounded transition-colors flex items-center justify-center gap-2"
                >
                  <FiEdit2 size={16} />
                  Editar
                </button>
                <button
                  onClick={() => handleDelete(ecosystem.id)}
                  className="flex-1 p-2 hover:bg-red-100 dark:hover:bg-red-950 text-red-600 dark:text-red-400 rounded transition-colors flex items-center justify-center gap-2"
                >
                  <FiTrash2 size={16} />
                  Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminEcosystem;
